// External Imports
import { Fragment, useEffect, useState } from "react";

// Internal Imports
import Heading from "../UI/Heading";
import { differenceInMinutes, endOfWeek, format, startOfWeek } from "date-fns";
import {
  MdAdd,
  MdChevronLeft,
  MdChevronRight,
  MdClose,
  MdFileDownload,
  MdFileOpen,
  MdRefresh,
  MdUploadFile,
} from "react-icons/md";
import { requestAdminAPI } from "../../api/posh-api";
import NoRecordsFound from "../UI/NoRecordsFound";
import { getISODate, isoDateToString } from "../../util/format-date";
import LoadingSpinner from "../UI/LoadingSpinner";
import Modal from "../UI/Modal";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import uploadFile from "../../util/upload-file";
import LinkPair from "../UI/LinkPair";

/** ========================================================================
 * * AttendanceGraph Component
 * This component renders .
 * ========================================================================== */

export default function AttendanceGraph({ employeeId }) {
  const [dates, setDates] = useState({
    sd: startOfWeek(new Date()),
    ed: endOfWeek(new Date()),
  });
  const defaultData = {
    Sun: [],
    Mon: [],
    Tue: [],
    Wed: [],
    Thu: [],
    Fri: [],
    Sat: [],
  };
  const [data, setData] = useState(defaultData);
  const [logs, setLogs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [addLogOpen, setAddLogOpen] = useState(false);
  const [uploadReportOpen, setUploadReportOpen] = useState(false);
  const [totalWage, setTotalWage] = useState(0);

  // ======================== FETCH DATA =======================
  const fetchRecords = async () => {
    try {
      setLoading(true);
      setData(defaultData);
      const response = await requestAdminAPI("filter", {
        table: "employeeAttendance",
        filters: [
          `employee: { id: { eq: "${employeeId}" } }`,
          `dateCreated: {ge: "${dates.sd.toISOString()}"}`,
          `dateCreated: {le: "${dates.ed.toISOString()}"}`,
        ],
      });
      // Get Reports
      const documents = await requestAdminAPI("filter", {
        table: "document",
        filters: [
          `employeeId: { eq: "${employeeId}" }`,
          `type: { eq: "Progress Report" }`,
          `dateCreated: {ge: "${dates.sd.toISOString()}"}`,
          `dateCreated: {le: "${dates.ed.toISOString()}"}`,
        ],
      });
      setLogs(response);

      let newCustom = {};
      let newDocuments = [];
      for (let i = 0; i < 7; i++) {
        // Determine Day of Week
        const date = new Date(dates.sd);
        date.setDate(date.getDate() + i);
        newCustom[format(date, "EEE")] = [];

        // Filter Logs for Day
        const logsOnDate = response
          .filter(
            (log) =>
              new Date(log.dateCreated).toDateString() === date.toDateString()
          )
          .sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));
        const documentsOnDate = documents
          .filter(
            (doc) =>
              new Date(doc.dateCreated).toDateString() === date.toDateString()
          )
          .sort((a, b) => new Date(a.dateCreated) - new Date(b.dateCreated));
        if (documentsOnDate.length > 0)
          newDocuments.push({
            link: documentsOnDate[documentsOnDate.length - 1].link,
            dateCreated:
              documentsOnDate[documentsOnDate.length - 1].dateCreated,
          });
        else newDocuments.push(null);

        if (logsOnDate.length > 1) {
          for (let j = 0; j < logsOnDate.length - 1; j++) {
            if (!logsOnDate[j].checkin) continue;
            if (logsOnDate[j + 1]) {
              const duration = differenceInMinutes(
                new Date(logsOnDate[j + 1].dateCreated),
                new Date(logsOnDate[j].dateCreated)
              );
              const spacer = differenceInMinutes(
                new Date(logsOnDate[j].dateCreated),
                logsOnDate[j - 1]
                  ? new Date(logsOnDate[j - 1].dateCreated)
                  : new Date(date).setHours(0, 0, 0)
              );
              const entry = {
                day: format(date, "EEE"),
                date: format(date, "MM/dd/yyyy"),
                duration: duration,
                spacer: spacer,
                checkin: format(new Date(logsOnDate[j].dateCreated), "hh:mm a"),
                checkout: format(
                  new Date(logsOnDate[j + 1].dateCreated),
                  "hh:mm a"
                ),
                row: i,
              };
              newCustom[format(date, "EEE")].push(entry);
            }
          }
          setData(newCustom);
        }
      }
      setDocuments(newDocuments);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // ================ Date Filter Handlers ================
  const nextWeek = () => {
    const change = new Date(dates.ed).setDate(dates.ed.getDate() + 1);
    setDates({ sd: startOfWeek(change), ed: endOfWeek(change) });
  };
  const prevWeek = () => {
    const change = new Date(dates.sd).setDate(dates.sd.getDate() - 1);
    setDates({ sd: startOfWeek(change), ed: endOfWeek(change) });
  };

  // Convert minutes to HH:MM for tooltip/labels
  const formatTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };
  const totalHours = formatTime(
    Object.keys(data).reduce(
      (acc, day) =>
        acc + data[day].reduce((acc, entry) => acc + entry.duration, 0),
      0
    )
  );

  // ======================== CALCULATE WAGE ========================
  const calculateWage = () => {
    const hourlyRate = parseFloat(
      document.getElementById("hourly")?.value || 0
    );
    const hours = parseInt(document.getElementById("hours")?.value || 0);
    const minutes = parseInt(document.getElementById("minutes")?.value || 0);
    const wage = hourlyRate * (hours + minutes / 60);
    if (isNaN(wage)) return;
    setTotalWage(wage.toFixed(2));
  };

  useEffect(() => {
    fetchRecords();
  }, [dates, employeeId]);

  return (
    <div className="grid gap-4">
      <Heading
        title="Attendance"
        isBackArrow={false}
        text="Following data is grouped by week. If there was no clock in, no data is shown. If there was no clock out, default is 5PM."
      >
        {loading ? (
          <LoadingSpinner classes="w-6 h-6 text-white" />
        ) : (
          <p className="text-stone-400">Total: {totalHours}</p>
        )}
        <div className="flex items-center space-x-4 text-sm">
          <MdChevronLeft className="icon" onClick={prevWeek} />
          <p>
            {format(dates.sd, "MM/dd/yyyy")} - {format(dates.ed, "MM/dd/yyyy")}
          </p>
          <MdChevronRight className="icon" onClick={nextWeek} />
        </div>
        <MdUploadFile
          className="icon"
          onClick={() => setUploadReportOpen(true)}
        />
        <MdRefresh className="icon" onClick={fetchRecords} />
      </Heading>

      <UploadReport
        open={uploadReportOpen}
        setOpen={setUploadReportOpen}
        employeeId={employeeId}
        refresh={fetchRecords}
      />
      <div className="grid w-full gap-4">
        {Object.keys(data).map((day, idx) => (
          <div
            key={idx}
            className="relative flex items-center h-[32px] w-[90%] mx-auto"
          >
            {loading ? (
              <div className="absolute w-full border animate-pulse border-stone-600"></div>
            ) : (
              <div className="absolute w-full border border-stone-600"></div>
            )}
            {documents[idx] && (
              <div className="absolute -left-16 text-stone-400" title={isoDateToString(documents[idx].dateCreated, "MM/dd/yyyy hh:mm a")}>
                <MdFileOpen
                  className="w-4 h-4 icon"
                  onClick={() => window.open(documents[idx].link, "_blank")}
                />
              </div>
            )}
            <p className="absolute -left-10 text-stone-400">{day}</p>
            {data[day].map((entry, idx) => {
              return (
                <Fragment key={idx}>
                  <div
                    className="rounded-md"
                    style={{
                      height: "32px",
                      width: `${(entry.spacer / 14.4).toFixed(2)}%`,
                    }}
                  ></div>
                  <Bar entry={entry} />
                </Fragment>
              );
            })}
            {!loading && (
              <p className="absolute -right-14 text-stone-400">
                {formatTime(
                  data[day].reduce((acc, entry) => acc + entry.duration, 0)
                )}
              </p>
            )}
          </div>
        ))}
      </div>

      <div className="info">
        <DetailPair
          edit={true}
          label="Hourly Rate ($)"
          inputOptions={{
            id: "hourly",
            type: "number",
            step: "0.01",
            onChange: calculateWage,
          }}
        />
        <DetailPair
          label="Total Hours (HH)"
          loading={loading}
          edit={true}
          inputOptions={{
            id: "hours",
            type: "number",
            defaultValue: totalHours.split(":")[0],
            onChange: calculateWage,
          }}
        />
        <DetailPair
          label="Total Minutes (MM)"
          edit={true}
          loading={loading}
          inputOptions={{
            id: "minutes",
            type: "number",
            defaultValue: totalHours.split(":")[1],
            onChange: calculateWage,
          }}
        />
        <DetailPair
          label="Salary ($)"
          loading={loading}
          edit={true}
          inputOptions={{
            id: "wage",
            disabled: true,
            value: totalWage || "",
          }}
        />
      </div>

      <AddLog
        open={addLogOpen}
        setOpen={setAddLogOpen}
        employeeId={employeeId}
        refresh={fetchRecords}
      />
      <Heading title="Logs" isBackArrow={false}>
        <MdAdd className="icon" onClick={() => setAddLogOpen(true)} />
      </Heading>
      {logs.length > 0 ? (
        <div className="gap-2 list">
          {logs.map((log, idx) => (
            <div key={idx} className="flex justify-between space-x-4 pill">
              <p>{isoDateToString(log.dateCreated, "MM/dd/yyyy hh:mm a")}</p>
              <p>{log.checkin ? "Check In" : "Check Out"}</p>
              <p>{log.fence || "Unknown Location"}</p>
            </div>
          ))}
        </div>
      ) : (
        <NoRecordsFound />
      )}
    </div>
  );
}

// ======================== BAR COMPONENT ========================
const Bar = ({ entry }) => {
  // Convert minutes to HH:MM for tooltip/labels
  const formatTime = (mins) => {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return `${h.toString().padStart(2, "0")}:${m.toString().padStart(2, "0")}`;
  };
  const [hovered, setHovered] = useState(false);
  return (
    <div
      className="relative bg-yellow-600 rounded-sm hover:bg-yellow-800"
      style={{
        height: "32px",
        width: `${Math.ceil(entry.duration / 14.4)}%`,
      }}
      onMouseEnter={() => setHovered(entry)}
      onMouseLeave={() => setHovered(false)}
    >
      {hovered && (
        <div className="absolute z-50 transition-all rounded-lg left-52 w-52">
          <div className="grid gap-1 p-4 text-sm rounded-md bg-stone-900 text-stone-300">
            <p>{`Date: ${hovered.date}`}</p>
            <p>{`Check In: ${hovered.checkin}`}</p>
            <p>{`Check Out: ${hovered.checkout}`}</p>
            <p>{`Duration: ${formatTime(hovered.duration)}`}</p>
          </div>
        </div>
      )}
    </div>
  );
};

// ======================== ADD LOG COMPONENT ========================
const AddLog = ({ open, setOpen, employeeId, refresh }) => {
  const [loading, setLoading] = useState(false);

  // ======================== SUBMIT HANDLER ========================
  const addLogHandler = async () => {
    try {
      setLoading(true);
      const payload = {
        checkin: document.getElementById("type").value === "Check In",
        time: getISODate(
          document.getElementById("date").value,
          document.getElementById("time").value
        ),
        employeeId: employeeId,
      };
      await requestAdminAPI("update-attendance", payload);
      refresh();
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:max-w-[600px]">
        <Heading title="Add Attendance Log" isBackArrow={false}>
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Date"
            edit={true}
            inputOptions={{
              type: "date",
              id: "date",
            }}
          />
          <DetailPair
            label="Time"
            edit={true}
            inputOptions={{
              type: "time",
              id: "time",
            }}
          />
          <SelectPair
            label="Type"
            edit={true}
            options={["Check In", "Check Out"]}
            inputOptions={{
              id: "type",
            }}
          />
          {loading ? (
            <LoadingSpinner />
          ) : (
            <button onClick={addLogHandler} className="btn-primary">
              Submit
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};

// ======================== ADD LOG COMPONENT ========================
const UploadReport = ({ open, setOpen, employeeId, refresh }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);

  // ======================== SUBMIT HANDLER ========================
  const uploadReportHandler = async () => {
    try {
      setLoading(true);
      const file = document.getElementById("reportFile").files[0];
      const link = await uploadFile(
        `employees/${employeeId}/report-${new Date().getTime()}`,
        file,
        setUploading
      );
      await requestAdminAPI("upload-document", {
        link: link,
        type: "Progress Report",
      });
      refresh();
      setOpen(false);
      setUploading(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal lg:max-w-[400px]">
        <Heading title="Upload Progress Report" isBackArrow={false}>
          <MdClose className="icon" onClick={() => setOpen(false)} />
        </Heading>
        <div className="grid gap-4 text-sm">
          <LinkPair
            label="Document"
            edit={true}
            inputOptions={{
              type: "file",
              id: "reportFile",
            }}
          />
          {loading ? (
            <div className="flex items-center space-x-4 text-sm text-stone-400">
              <LoadingSpinner />
              {uploading ? <p>Uploading file... {uploading}%</p> : null}
            </div>
          ) : (
            <button onClick={uploadReportHandler} className="btn-primary">
              Submit
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
};
